import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Play,
  ExternalLink,
  NewspaperIcon,
  TrendingUp,
  Clock,
  Eye,
  Share2,
  Bookmark,
  ChevronRight,
  Calendar,
  Users,
  Star,
  Bell,
  ArrowUpRight
} from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { useApp } from '@/pages/providers/AppProvider';

interface NewsItem {
  id: string;
  title: string;
  link: string;
  type: 'youtube' | 'link' | 'article' | 'announcement' | 'image' | 'banner' | 'svg' | 'poster' | 'video';
  description?: string;
  thumbnail?: string;
  published?: string;
  category?: string;
  views?: number;
  isNew?: boolean;
  isPinned?: boolean;
  author?: string;
  duration?: number;
  isActive?: boolean;
}

interface NewsAnnouncementEnhancedProps {
  newsItems?: NewsItem[];
  showViewCount?: boolean;
  enableBookmarks?: boolean;
  maxItems?: number;
}

export const NewsAnnouncementEnhanced: React.FC<NewsAnnouncementEnhancedProps> = ({
  newsItems = [],
  showViewCount = true,
  enableBookmarks = true,
  maxItems = 5
}) => {
  const { contentVersion } = useApp();
  const [managedNewsItems, setManagedNewsItems] = useState<NewsItem[]>([]);
  const [bookmarkedItems, setBookmarkedItems] = useState<string[]>([]);
  const [headerTitle, setHeaderTitle] = useState<string>('News & Announcements');

  // Carousel state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeItems, setActiveItems] = useState<NewsItem[]>([]);

  // Load news items form backend
  useEffect(() => {
    (async () => {
      try {
        const res = await apiClient.getNews({ status: 'all', limit: 20 });
        const items = Array.isArray((res as any)?.news)
          ? (res as any).news
          : (Array.isArray(res) ? (res as any) : []);

        if (Array.isArray(items)) {
          const normalized = items.map((it: any) => ({
            ...it,
            isActive: it?.isActive !== undefined ? !!it.isActive : (it?.status ? String(it.status).toLowerCase() === 'active' : true),
            duration: it.duration ? Number(it.duration) : 5
          }));
          const active = normalized.filter((item: any) => item.isActive !== false);

          // Sort items: pinned first, then by date
          active.sort((a: NewsItem, b: NewsItem) => {
            if (a.isPinned && !b.isPinned) return -1;
            if (!a.isPinned && b.isPinned) return 1;
            return 0; // Maintain original order (created desc)
          });

          setManagedNewsItems(active);
          // Filter capabilities for the carousel (visual items or important announcements)
          // For now, put everything in the carousel cycle
          setActiveItems(active.slice(0, maxItems));
        }
      } catch (error) {
        console.error('Error loading news items:', error);
      }

      // Load editable header
      try {
        const settings = await apiClient.getSectionSettings('news_announcement');
        const title = settings?.config?.headerTitle;
        if (title && typeof title === 'string') {
          setHeaderTitle(title);
        }
      } catch (err) { }
    })();

    // Load bookmarks (protected by auth check inside client or fail gracefully)
    (async () => {
      try {
        const bookmarks = await apiClient.getBookmarkedNews();
        if (Array.isArray(bookmarks)) {
          setBookmarkedItems(bookmarks);
        }
      } catch (error) {
        // silent fail if not logged in
      }
    })();
  }, [contentVersion, maxItems]);

  const handleToggleBookmark = async (e: React.MouseEvent, newsId: string) => {
    e.stopPropagation();
    const isBookmarked = bookmarkedItems.includes(newsId);

    // Optimistic update
    if (isBookmarked) {
      setBookmarkedItems(prev => prev.filter(id => id !== newsId));
    } else {
      setBookmarkedItems(prev => [...prev, newsId]);
    }

    try {
      await apiClient.toggleBookmarkedNews(newsId);
    } catch (error) {
      // Revert
      if (isBookmarked) {
        setBookmarkedItems(prev => [...prev, newsId]);
      } else {
        setBookmarkedItems(prev => prev.filter(id => id !== newsId));
      }
    }
  };

  // Auto-scroll logic
  useEffect(() => {
    if (activeItems.length <= 1) return;

    const currentItem = activeItems[currentIndex];
    const duration = (currentItem?.duration || 5) * 1000;

    const timer = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % activeItems.length);
    }, duration);

    return () => clearTimeout(timer);
  }, [currentIndex, activeItems]);

  const currentItem = activeItems[currentIndex];

  const getYouTubeVideoId = (url: string) => {
    if (!url) return null;
    const m1 = url.match(/[?&]v=([^&]+)/);
    if (m1 && m1[1]) return m1[1];
    const m2 = url.match(/youtu\.be\/([^?&#\s]+)/);
    if (m2 && m2[1]) return m2[1];
    const m3 = url.match(/embed\/([a-zA-Z0-9_-]+)/);
    return m3 ? m3[1] : null;
  };

  const getYouTubeThumbnail = (url: string) => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
  };

  const getYouTubeEmbedUrl = (url: string) => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${videoId}` : null;
  };

  return (
    <div className="w-full space-y-3">
      {/* Featured Content Carousel */}
      {currentItem && (
        <Card className="bg-white shadow-lg rounded-xl border-0 overflow-hidden group hover:shadow-xl transition-all duration-300">
          <CardHeader className="pb-2 bg-gradient-to-r from-red-50 to-pink-50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xs flex items-center gap-1.5 bg-red-500 text-white px-3 py-1.5 rounded-full font-medium truncate max-w-[200px]">
                {currentItem.type === 'youtube' ? <Play className="w-3 h-3" /> : <Bell className="w-3 h-3" />}
                {headerTitle}
              </CardTitle>
              {/* Type badge removed per user request */}
            </div>
          </CardHeader>
          <CardContent className="p-3 pt-2">
            <div className="space-y-2 animate-fadeIn key={currentItem.id}">

              {/* Media Content */}
              <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden group-hover:scale-[1.02] transition-transform duration-300">
                {currentItem.type === 'youtube' ? (
                  // YouTube Logic
                  getYouTubeEmbedUrl(currentItem.link) ? (
                    <iframe
                      src={getYouTubeEmbedUrl(currentItem.link) || undefined}
                      title={currentItem.title}
                      className="w-full h-full pointer-events-none" // Disable interaction for auto-play feel, or remove pointer-events-none if interaction needed
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <img
                      src={currentItem.thumbnail || getYouTubeThumbnail(currentItem.link) || ''}
                      alt={currentItem.title}
                      className="w-full h-full object-cover"
                    />
                  )
                ) : (['image', 'banner', 'poster', 'svg'].includes(currentItem.type) && (currentItem.thumbnail || currentItem.link)) ? (
                  // Image Logic
                  <a href={currentItem.link} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                    <img
                      src={currentItem.thumbnail || currentItem.link}
                      alt={currentItem.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  </a>
                ) : (
                  // Fallback / Text Only
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4 text-center">
                    <Bell className="w-8 h-8 text-gray-300 mb-2" />
                    <p className="text-sm font-medium text-gray-500 line-clamp-3">{currentItem.description || currentItem.title}</p>
                  </div>
                )}

                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover:bg-black/10 transition-colors">
                  <div
                    className="flex items-center justify-center w-12 h-12 bg-black/40 hover:bg-red-600 text-white rounded-full cursor-pointer backdrop-blur-sm transition-all duration-300 pointer-events-auto hover:scale-110"
                    onClick={() => window.open(currentItem.link, '_blank')}
                  >
                    <Play className="w-5 h-5 fill-white ml-0.5" />
                  </div>
                </div>

                {/* Progress Indicator (Optional/polishing) */}
                <div className="absolute bottom-0 left-0 h-1 bg-red-500/20 w-full">
                  <div
                    className="h-full bg-red-500 transition-all ease-linear"
                    style={{
                      width: '100%',
                      transitionDuration: `${(currentItem.duration || 5)}s`
                      // Note: precise sync with CSS transition for keyframe based progress bar is complex, 
                      // simpler to leave it or use a separate key-based component.
                      // For now, simpler is better.
                    }}
                  />
                </div>
              </div>

              {/* Metadata */}
              <div className="space-y-1">
                <a
                  href={currentItem.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-blue-700 hover:underline line-clamp-2 block"
                >
                  {currentItem.title}
                </a>
                {currentItem.description && (
                  <p className="text-xs text-gray-600 line-clamp-2">
                    {currentItem.description}
                  </p>
                )}
                <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3 h-3" />
                    <span>{currentItem.published || 'Recent'}</span>
                  </div>
                  {activeItems.length > 1 && (
                    <div className="flex gap-1">
                      {activeItems.map((_, idx) => (
                        <span
                          key={idx}
                          className={`block w-1.5 h-1.5 rounded-full ${idx === currentIndex ? 'bg-red-500' : 'bg-gray-300'}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Enhanced Quick Links Section */}
      <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-lg" {...({} as any)}>
        <CardHeader className="p-2 pb-0">
          <CardTitle className="text-xs flex items-center gap-1.5 bg-green-500 text-white px-3 py-1.5 rounded-full font-medium w-fit">
            <Users className="w-3 h-3" />
            Quick Access
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2 pt-0">
          <div className="space-y-1 -mt-1">
            {[
              { label: 'Browse All Jobs', url: '/jobs', icon: '🔍' },
              { label: 'Top Companies', url: '/companies', icon: '🏢' },
              { label: 'Career Resources', url: '/career-guide', icon: '📚' },
              { label: 'Salary Guide', url: '/salary-guide', icon: '💰' }
            ].map((link, index) => (
              <Button
                key={index}
                variant="ghost"
                size="sm"
                className="w-full justify-start text-xs p-2 h-auto rounded-lg hover:bg-white/60 transition-all duration-200 group"
                onClick={() => window.open(`${window.location.origin}${link.url}`, '_blank')}
              >
                <span className="mr-2 group-hover:scale-110 transition-transform duration-200">
                  {link.icon}
                </span>
                {link.label}
                <ChevronRight className="w-3 h-3 ml-auto text-gray-400 group-hover:text-green-600 group-hover:translate-x-0.5 transition-all duration-200" />
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
export { NewsAnnouncementEnhanced as NewsAnnouncement };
